import { useEffect, useRef, useState } from 'react'
import { QueryError, QueryRow } from './types'
import { QueryResultsTable } from './QueryResultsTable'
import { useTar } from '@/modules/i18n'
import { ClockIcon } from '@heroicons/react/24/outline'

interface ResultsDisplayProps {
  queryResults?: QueryRow[];
  queryError?: QueryError;
  tableMode?: boolean;
  isVisible?: boolean;
  isExecuting?: boolean;
}

const processQueryError = (queryError: QueryError): QueryError => {
  const [, errorMessage] = /^error: (.*)$/.exec(queryError.error) || []
  return {
    error: errorMessage ? `SQL Error: ${errorMessage}` : queryError.error,
    hint: queryError.hint,
    errorCode: queryError.errorCode,
  }
}

const RECT_COUNT = 5
const STEP_COUNT = RECT_COUNT + 1 // 0..4 fill, 5 = fade/reset
const STEP_DURATION_MS = 500 // ~150ms fill + 100ms pause

const SpecialQueryLoader = () => {
  const { t } = useTar()
  const [elapsedMs, setElapsedMs] = useState(0)
  const [step, setStep] = useState(0)
  const startTimeRef = useRef<number | null>(null)

  // High-resolution timer that runs up with milliseconds
  useEffect(() => {
    let frameId: number

    const tick = (now: number) => {
      if (startTimeRef.current == null) {
        startTimeRef.current = now
      }
      setElapsedMs(now - startTimeRef.current)
      frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [])

  // Sequenced rectangle filling animation
  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % STEP_COUNT)
    }, STEP_DURATION_MS)

    return () => clearInterval(interval)
  }, [])

  const seconds = (elapsedMs / 1000).toFixed(1)

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-sm font-medium text-primary italic">
        {t('challenges.executingQuery')}
      </div>
      <div className="font-mono text-xs text-muted-foreground">
        {seconds}s
      </div>
      <div className="flex items-center gap-2">
        {Array.from({ length: RECT_COUNT }).map((_, index) => {
          const isFilled = step >= index && step < RECT_COUNT

          return (
            <div
              key={index}
              className={[
                'h-3 w-6 rounded-sm border border-primary/80',
                'transition-colors duration-700',
                isFilled ? 'bg-primary' : 'bg-transparent',
              ].join(' ')}
            />
          )
        })}
      </div>
    </div>
  )
}

export const QueryResults = ({
  queryResults,
  queryError,
  tableMode = false,
  isVisible = true,
  isExecuting = false,
}: ResultsDisplayProps) => {
  const { t } = useTar()

  // Special handling for query timeout – show a clear notice about max time reached
  if (queryError?.errorCode === 'queryTimeout') {
    return (
      <div className="h-full min-w-0" data-tour-id="query-results">
        <div className="p-4">
          <div className="bg-error/10 border border-error/20 rounded-lg p-6 min-h-[120px]">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-error" />
              </div>
              <div className="flex-1 space-y-2">
                <h3 className="text-base font-semibold text-error">
                  {t('errors.queryTimeout')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('errors.queryTimeoutDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Disallowed patterns (e.g. SET LOCAL, writes) – show a clear notice
  if (queryError?.errorCode === 'disallowedPatterns') {
    return (
      <div className="h-full min-w-0" data-tour-id="query-results">
        <div className="p-4">
          <div className="bg-error/10 border border-error/20 rounded-lg p-6 min-h-[120px]">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-error" />
              </div>
              <div className="flex-1 space-y-2">
                <h3 className="text-base font-semibold text-error">
                  {t('errors.disallowedPatterns')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('errors.disallowedPatternsDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const processedQueryError = queryError ? processQueryError(queryError) : null
  if (isExecuting) {
    return (
      <div className="h-full min-w-0 flex items-center justify-center">
        <SpecialQueryLoader />
      </div>
    )
  }
  return (
    <div className="h-full min-w-0" dir="ltr" data-tour-id="query-results">
      {processedQueryError ? (
        <div className="p-4">
          <div className="bg-error/10 border border-error/20 rounded-sm p-4 min-h-[100px] flex items-center">
            <div className="text-error text-sm font-mono whitespace-pre-wrap">
              {processedQueryError.error}
              {processedQueryError.hint && (
                <div className="text-xs text-muted-foreground">
                  {processedQueryError.hint}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <QueryResultsTable queryResults={queryResults || []} tableMode={tableMode} isVisible={isVisible} />
      )}
    </div>
  )
}